import { HttpClient } from "@utils/httpClient";
import type { SearchResponse } from "@models/search";

const BASE_URL = import.meta.env.VITE_SEARCH_API_BASE_URL ?? "";


export interface ValueSearchParams {
  /** Vector index to query — the job's `pluginId`. */
  indexId: string;
  /** Pins the field. The search service resolves its label from the manifest,
   *  so the caller does not send `itemMpText` — see {@link SearchApi.searchValues}. */
  itemMpId: number;
  value: string;
  topK?: number;
}

/**
 * Client for `xplatform_search_service`. The mapping frontend uses it to look
 * up the catalogue values closest to a reviewer-typed value before adding a
 * manual row; the mapping service itself never queries the vector index.
 */
export class SearchApi {
  private httpClient: HttpClient;

  constructor(httpClient?: HttpClient) {
    this.httpClient = httpClient ?? new HttpClient(BASE_URL);
  }

  /**
   * Closest catalogue values for the typed `value`, restricted to the picked
   * `itemMpId`. Best match first.
   *
   * **The query is the value alone.** It used to be `"{itemMpText} - {value}"`,
   * which the search service then scored the hits against as if it were "the
   * user's own words" — but those words already contained the catalogue's own
   * label, so the record matched the half of the query that came from the
   * catalogue in the first place. Composing the retrieval pattern is the search
   * service's job: the field is pinned by the filter, and it resolves that
   * field's label from the index manifest.
   *
   * Six hits is enough to REVIEW with; it is not enough to conclude a value is
   * absent from the catalogue, and this client no longer implies otherwise —
   * that verdict comes from `exact_value_match`, which the search service
   * decides over its whole retrieval pool.
   */
  async searchValues({
    indexId,
    itemMpId,
    value,
    topK = 6,
  }: ValueSearchParams): Promise<SearchResponse> {
    return this.httpClient.makeAuthenticatedRequest<SearchResponse>("/search", {
      method: "POST",
      body: JSON.stringify({
        index_id: indexId,
        query: { type: "text", text: value },
        top_k: topK,
        filter: { item_mp_id: itemMpId },
      }),
    });
  }

}

export const searchApi = new SearchApi();
